/* eslint-disable no-undefined */
/* eslint-disable func-style */
import React from "react";
import { Tabs as TabsMUI, Tab, Badge } from "@mui/material";
import { Link } from "react-router-dom";
import type { SxProps, Theme } from "@mui/material/styles";

type TabContext<T> = {
  value:T;
  label:React.ReactNode;
  badgeContents?: React.ReactNode;
  badgeDataCy?: string; // optional data-cy for MUI Badge
  href?: string; // when present, this tab will render as a Link
}

type TabProps<T> = {
  value:T;
  tabs:TabContext<T>[];
  setValue?: (value:T) => void; // optional when using href link mode
  sx?: SxProps<Theme>;
}

const badgeSx = {
  "& .MuiBadge-badge": {
    top: 8,
    right: -16,
    padding: "0 4px",
    margin: 0,
    fontSize: 12,
    color: "white",
    backgroundColor: (theme: Theme) => theme.palette.primary.main,
  },
};

const tabBaseSx = {
  textTransform: "none",
  color: "black",
  fontWeight: 600,
  fontSize: "90%",
  minWidth: 0,
  "&.Mui-selected": {
    color: (theme: Theme) => theme.palette.primary.main,
  },
};

const tabWithBadgeSx = {
  ...tabBaseSx,
  overflow: "visible",
  pr: 4, // extra right padding to accommodate badge width
};

export default function Tabs<T extends React.Key> (
  { value, setValue, tabs, sx }: Readonly<TabProps<T>>,
): JSX.Element {
  const hasHref = tabs.some(t => Boolean(t.href));
  const tabsProps: Partial<React.ComponentProps<typeof TabsMUI>> = (!hasHref && setValue) ? ({
    onChange: (_event, val) => setValue(val as T),
    sx,
  }) : { sx };
  return (
    <TabsMUI
      value={value}
      indicatorColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      {...tabsProps}
    >
      {tabs.map(tab => {
        const linkProps = tab.href ? ({ component: Link, to: tab.href } as const) : ({} as const);
        return tab.badgeContents ? (
          <Tab
            key={tab.value}
            label={
              <Badge
                variant="standard"
                color="primary"
                badgeContent={tab.badgeContents}
                showZero
                sx={badgeSx}
                data-cy={tab.badgeDataCy}
              >
                {tab.label}
              </Badge>
            }
            value={tab.value}
            sx={tabWithBadgeSx}
            {...linkProps}
          />
        ) : (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            sx={tabBaseSx}
            {...linkProps}
          />
        );
      })}
    </TabsMUI>
  );
}
